import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router'
import { HowTo } from './components/HowTo.tsx'
import { DesktopNav, ErrorNote, Loading, NotFound, ToastProvider, applyTheme, currentTheme } from './components/shell.tsx'
import { prefetchToday, useMe } from './lib/data.ts'
import { captureFirstTouch } from './lib/first-touch.ts'
import { captureInstallPrompt } from './lib/install.ts'
import { useIsDesktop } from './lib/layout.ts'
import { refreshReminder, startReminders } from './lib/reminders.ts'
import AdminPage from './pages/Admin.tsx'
import ChallengePage from './pages/Challenge.tsx'
import DiscussionPage from './pages/Discussion.tsx'
import GamePage from './pages/Game.tsx'
import LeaderboardPage from './pages/Leaderboard.tsx'
import ProfilePage from './pages/Profile.tsx'
import ResultsPage from './pages/Results.tsx'
import RivalryPage from './pages/Rivalry.tsx'
import RivalsPage from './pages/Rivals.tsx'
import SignInPage from './pages/SignIn.tsx'
import SlowBurnPage from './pages/SlowBurn.tsx'
import SignInConfirmPage from './pages/SignInConfirm.tsx'
import TodayPage from './pages/Today.tsx'
import YouPage from './pages/You.tsx'
import './styles/kit.css'
import './styles/app.css'

function App() {
  const desktop = useIsDesktop()
  const meQ = useMe()
  const me = meQ.data
  const { pathname } = useLocation()
  const [howto, setHowto] = useState(false)
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  useEffect(() => {
    // Follow the system theme while the choice is "auto".
    const mq = matchMedia('(prefers-color-scheme: dark)')
    const on = () => { if (currentTheme() === 'auto') applyTheme('auto') }
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  // A daily reminder that's on follows whoever plays here (it needs /me's session first).
  const playerId = me?.id
  useEffect(() => { if (playerId) refreshReminder(playerId) }, [playerId])
  // Load /me first: on a first visit it creates the guest and sets the session cookie, so the
  // screen's other requests all belong to that one guest.
  if (!me) return <div className="app">{meQ.error ? <div className="page"><ErrorNote error={meQ.error} retry={() => meQ.reload()} /></div> : <div className="page"><Loading /></div>}</div>
  return (
    <div className={`app${desktop ? ' is-desktop' : ''}`}>
      {desktop && <DesktopNav me={me} onHelp={() => setHowto(true)} />}
      {desktop && <HowTo open={howto} onClose={() => setHowto(false)} />}
      <Routes>
        <Route path="/" element={<TodayPage />} />
        <Route path="/play" element={<GamePage />} />
        <Route path="/play/:number" element={<GamePage />} />
        <Route path="/results/:number" element={<ResultsPage />} />
        <Route path="/slow-burn" element={<SlowBurnPage />} />
        <Route path="/slow-burn/:number" element={<SlowBurnPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/you/:tab?" element={<YouPage />} />
        <Route path="/discussion/:number" element={<DiscussionPage />} />
        <Route path="/c/:code" element={<ChallengePage />} />
        <Route path="/rivals" element={<RivalsPage />} />
        <Route path="/rivals/:id" element={<RivalryPage />} />
        <Route path="/p/:handle" element={<ProfilePage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signin/confirm" element={<SignInConfirmPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  )
}

captureFirstTouch()
// The Today screen waits for /me; its edition needn't.
if (location.pathname === '/') prefetchToday()
captureInstallPrompt()
startReminders()
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <App />
      </ToastProvider>
    </BrowserRouter>
  </StrictMode>,
)
